import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPermitTypesForLocation } from '../config/permitTypesConfig';
import './PermitTypeSelector.css';

const PermitTypeSelector = () => {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState('');
  const [permitTypes, setPermitTypes] = useState([]);
  const [locationInfo, setLocationInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get selected county/city from localStorage
    const selectedCountyData = localStorage.getItem('selectedCounty');
    
    if (selectedCountyData) {
      try {
        const location = JSON.parse(selectedCountyData);
        setLocationInfo(location);
        
        // Get permit types for this county/city
        const availableTypes = getPermitTypesForLocation(location.county, location.city);
        setPermitTypes(availableTypes);
        
        if (availableTypes.length === 0) {
          console.warn(`No permit types configured for ${location.city}, ${location.county}`);
        }
      } catch (error) {
        console.error('Failed to parse selected county:', error);
        // Fallback to default permit types
        const defaultTypes = getPermitTypesForLocation('default');
        setPermitTypes(defaultTypes);
      }
    } else {
      // No county selected - should not happen due to ProtectedRoute, but handle gracefully
      console.warn('No county selected. Redirecting to county selector...');
      navigate('/select');
      return;
    }
    
    setLoading(false);
  }, [navigate]);

  const handleContinue = () => {
    if (selectedType) {
      const selectedPermitType = permitTypes.find(type => type.id === selectedType);
      if (selectedPermitType) {
        // Pass location and selected permitType into next route
        navigate(selectedPermitType.route, {
          state: {
            permitType: selectedType,
            county: locationInfo?.county,
            city: locationInfo?.city
          }
        });
      } else {
        // Fallback to old route format
        navigate(`/submit/${selectedType}`, { state: { permitType: selectedType } });
      }
    }
  };

  if (loading) {
    return (
      <div className="permit-type-selector-container">
        <div className="permit-type-selector-card">
          <div className="selector-header">
            <h1>Loading...</h1>
          </div>
        </div>
      </div>
    );
  }

  if (permitTypes.length === 0) {
    return (
      <div className="permit-type-selector-container">
        <div className="permit-type-selector-card">
          <div className="selector-header">
            <h1>No Permit Types Available</h1>
            <p>No permit types are configured for {locationInfo?.city}, {locationInfo?.county}.</p>
            <p>Please contact support or select a different location.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="permit-type-selector-container">
      <div className="permit-type-selector-card">
        <div className="selector-header">
          <h1>Select Permit Type</h1>
          <p>Choose the type of permit you would like to apply for</p>
          {locationInfo && (
            <p className="location-info">
              Location: <strong>{locationInfo.city}, {locationInfo.county}</strong>
            </p>
          )}
        </div>

        <div className="permit-types-grid">
          {permitTypes.map((type) => (
            <div
              key={type.id}
              className={`permit-type-card ${selectedType === type.id ? 'selected' : ''}`}
              onClick={() => setSelectedType(type.id)}
            >
              <div className="permit-type-icon">{type.icon}</div>
              <div className="permit-type-name">{type.name}</div>
              <div className="permit-type-description">{type.description}</div>
            </div>
          ))}
        </div>

        {selectedType && (
          <div className="selector-actions">
            <button className="continue-button" onClick={handleContinue}>
              Continue to Application →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PermitTypeSelector;
